import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Search,
  WarningAmber,
  EventAvailable,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { patientService, authService } from '../../services/api';
import TopBar from '../../components/TopBar';

const PatientManagement = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
    email: '',
    address: '',
    bloodGroup: '',
    emergencyContact: '',
    lmp: '',
    edd: '',
    hindiPrescription: false,
    marathiPrescription: false,
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [conflictInfo, setConflictInfo] = useState(null);    // 409 payload
  const [pendingPayload, setPendingPayload] = useState(null); // saved for force-create

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const response = await patientService.getAllPatients();
      setPatients(response.data);
    } catch (err) {
      setError('Failed to load patients');
    }
  };

  const resetForm = () => ({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
    email: '',
    address: '',
    bloodGroup: '',
    emergencyContact: '',
    lmp: '',
    edd: '',
    hindiPrescription: false,
    marathiPrescription: false,
  });

  const handleOpenDialog = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
        gender: patient.gender || '',
        phoneNumber: patient.phoneNumber || '',
        email: patient.email || '',
        address: patient.address || '',
        bloodGroup: patient.bloodGroup || '',
        emergencyContact: patient.emergencyContact || '',
        lmp: patient.lmp ? patient.lmp.split('T')[0] : '',
        edd: patient.edd ? patient.edd.split('T')[0] : '',
        hindiPrescription: patient.hindiPrescription || false,
        marathiPrescription: patient.marathiPrescription || false,
      });
    } else {
      setEditingPatient(null);
      setFormData(resetForm());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPatient(null);
    setFormData(resetForm());
    setConflictInfo(null);
    setPendingPayload(null);
  };

  const calculateEDD = (lmpValue) => {
    if (!lmpValue) return '';
    const lmpDate = new Date(lmpValue);
    lmpDate.setDate(lmpDate.getDate() + 280);
    return lmpDate.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === 'lmp') {
      updated.edd = calculateEDD(value);
    }
    setFormData(updated);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const buildPayload = () => {
    const payload = { ...formData };
    if (!payload.lmp) payload.lmp = null;
    if (!payload.edd) payload.edd = null;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();

      if (editingPatient) {
        payload.id = editingPatient.id;
        await patientService.updatePatient(editingPatient.id, payload);
        setSuccess('Patient updated successfully!');
        handleCloseDialog();
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        await patientService.createPatient(payload);
        setSuccess('Patient added successfully!');
        handleCloseDialog();
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        // Duplicate found — store conflict info and show confirmation dialog
        setPendingPayload(buildPayload());
        setConflictInfo(err.response.data);
      } else {
        setError(err.response?.data?.message || 'Failed to save patient');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // User chose to register anyway despite the duplicate warning
  const handleForceCreate = async () => {
    try {
      await patientService.createPatient(pendingPayload, true);
      setConflictInfo(null);
      setPendingPayload(null);
      setSuccess('Patient registered successfully!');
      handleCloseDialog();
      loadPatients();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save patient');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBookAppointment = () => {
    const patientId = conflictInfo?.existingPatient?.id;
    setConflictInfo(null);
    setPendingPayload(null);
    handleCloseDialog();
    if (patientId) {
      navigate('/appointments', { state: { openNewWithPatientId: patientId } });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await patientService.deletePatient(id);
        setSuccess('Patient deleted successfully!');
        loadPatients();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete patient');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Filter patients based on search text
  const filteredPatients = patients.filter((patient) => {
    if (!searchText) return true;
    
    const searchLower = searchText.toLowerCase();
    
    // Search across all relevant fields
    return (
      patient.id?.toString().includes(searchLower) ||
      patient.firstName?.toLowerCase().includes(searchLower) ||
      patient.lastName?.toLowerCase().includes(searchLower) ||
      patient.gender?.toLowerCase().includes(searchLower) ||
      patient.phoneNumber?.toLowerCase().includes(searchLower) ||
      patient.email?.toLowerCase().includes(searchLower) ||
      patient.bloodGroup?.toLowerCase().includes(searchLower) ||
      patient.address?.toLowerCase().includes(searchLower) ||
      patient.emergencyContact?.toLowerCase().includes(searchLower) ||
      patient.age?.toString().includes(searchLower)
    );
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 55 },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 100 },
    { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 100 },
    {
      field: 'age',
      headerName: 'Age',
      width: 55,
      valueGetter: (params) => params.row.age || '-',
    },
    { field: 'gender', headerName: 'Gender', width: 75 },
    { field: 'phoneNumber', headerName: 'Phone', width: 120 },
    { field: 'bloodGroup', headerName: 'Blood Grp', width: 85 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 130 },
    {
      field: 'lmp',
      headerName: 'LMP',
      width: 100,
      valueGetter: (params) =>
        params.row.lmp ? new Date(params.row.lmp).toLocaleDateString('en-IN') : '-',
    },
    {
      field: 'edd',
      headerName: 'EDD',
      width: 100,
      valueGetter: (params) =>
        params.row.edd ? new Date(params.row.edd).toLocaleDateString('en-IN') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Patient Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              All Patients
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              }}
            >
              New Patient
            </Button>
          </Box>

          {/* Search Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search patients by name, phone, email, blood group, etc..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Search sx={{ color: 'action.active', mr: 1 }} />
                ),
              }}
              variant="outlined"
              size="medium"
            />
          </Box>

          <DataGrid
            rows={filteredPatients}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Add / Edit Patient Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Blood Group"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Blood Group</MenuItem>
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LMP (Last Menstrual Period)"
                  name="lmp"
                  type="date"
                  value={formData.lmp}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.gender === 'Male'}
                  helperText={formData.gender === 'Male' ? 'Not applicable for Male' : 'Optional - select date from calendar'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EDD (Estimated Delivery Date)"
                  name="edd"
                  type="date"
                  value={formData.edd}
                  InputLabelProps={{ shrink: true }}
                  helperText="Auto-calculated from LMP (LMP + 280 days)"
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel component="legend" sx={{ mb: 0.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                  Prescription Language
                </FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="hindiPrescription"
                        checked={Boolean(formData.hindiPrescription)}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label="Hindi Prescription"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="marathiPrescription"
                        checked={Boolean(formData.marathiPrescription)}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label="Marathi Prescription"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPatient ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Duplicate Conflict Confirmation Dialog */}
      <Dialog open={!!conflictInfo} onClose={() => { setConflictInfo(null); setPendingPayload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningAmber color="warning" />
          Duplicate Patient Detected
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {conflictInfo?.message}
          </Alert>
          {conflictInfo?.existingPatient && (
            <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Existing Patient Record</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {conflictInfo.existingPatient.firstName} {conflictInfo.existingPatient.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Gender</Typography>
                  <Typography variant="body2">{conflictInfo.existingPatient.gender}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body2">
                    {conflictInfo.existingPatient.dateOfBirth
                      ? new Date(conflictInfo.existingPatient.dateOfBirth).toLocaleDateString('en-IN')
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2">{conflictInfo.existingPatient.phoneNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient ID</Typography>
                  <Typography variant="body2">#{conflictInfo.existingPatient.id}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setConflictInfo(null); setPendingPayload(null); }}>
            Go Back &amp; Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EventAvailable />}
            onClick={handleBookAppointment}
          >
            Book Appointment
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Add />}
            onClick={handleForceCreate}
          >
            Register Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PatientManagement;
